import { t } from '@lingui/core/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { concatFloat32, encodeWav } from './audio-recorder/encodeWav'
import { type MicCheckResult, SILENCE_MAX_ABS, analyzeBuffer, peak } from '../components/mic-check/micCheckAnalysis'

export type RecordingState = 'idle' | 'recording' | 'recorded' | 'submitting' | 'submitted'

export type RecordingQuality = {
  result: MicCheckResult
  blocked: boolean
}

const WORKLET_URL = '/audio/pcm-recorder-worklet.js'
const WORKLET_NAME = 'pcm-recorder'
const LIVENESS_CHECK_MS = 1500

const sanitizeMetric = (x: number): number | null => (Number.isFinite(x) ? Math.round(x * 100) / 100 : null)

type AudioSettings = Pick<
  MediaTrackSettings,
  'deviceId' | 'groupId' | 'sampleRate' | 'channelCount' | 'echoCancellation' | 'noiseSuppression' | 'autoGainControl'
>

type AudioClientMetadata = Partial<AudioSettings> & {
  deviceLabel?: string
  encodedFormat?: 'wav'
  bitsPerSample?: number
  encodedSampleRate?: number
  kickstart?: boolean
  ctxState?: string
  trackMutedAtStart?: boolean
  trackMutedAtStop?: boolean
  clientQuality?: {
    issue: string | null
    voiceRatio: number | null
    peakDb: number | null
    voiceDb: number | null
    snr: number | null
  }
}

const safeDisconnect = (node: AudioNode | null | undefined) => {
  if (!node) return
  try {
    node.disconnect()
  } catch (e) {
    void e
  }
}

const readAudioClientMetadata = (stream: MediaStream): AudioClientMetadata => {
  const track = stream.getAudioTracks()[0]
  const { deviceId, groupId, sampleRate, channelCount, echoCancellation, noiseSuppression, autoGainControl } =
    (track?.getSettings() ?? {}) as Partial<AudioSettings>
  const meta: AudioClientMetadata = {}
  if (track?.label) meta.deviceLabel = track.label
  if (deviceId !== undefined) meta.deviceId = deviceId
  if (groupId !== undefined) meta.groupId = groupId
  if (sampleRate !== undefined) meta.sampleRate = sampleRate
  if (channelCount !== undefined) meta.channelCount = channelCount
  if (echoCancellation !== undefined) meta.echoCancellation = echoCancellation
  if (noiseSuppression !== undefined) meta.noiseSuppression = noiseSuppression
  if (autoGainControl !== undefined) meta.autoGainControl = autoGainControl
  return meta
}

type UseAudioRecorderOptions = {
  captureMetadata?: boolean
}

export function useAudioRecorder({ captureMetadata = false }: UseAudioRecorderOptions = {}) {
  const [state, setState] = useState<RecordingState>('idle')
  const [seconds, setSeconds] = useState(0)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [quality, setQuality] = useState<RecordingQuality | null>(null)
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)

  const audioCtxRef = useRef<AudioContext | null>(null)
  const workletReadyRef = useRef(false)
  const streamRef = useRef<MediaStream | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const sinkRef = useRef<GainNode | null>(null)
  const kickstartElRef = useRef<HTMLAudioElement | null>(null)
  const analyserNodeRef = useRef<AnalyserNode | null>(null)
  const chunksRef = useRef<Float32Array[]>([])
  const sampleRateRef = useRef<number>(48000)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const livenessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startingRef = useRef(false)
  const stoppingRef = useRef(false)
  const metaRef = useRef<AudioClientMetadata | null>(null)
  const stopRef = useRef<() => void>(() => {})

  const teardownGraph = useCallback(() => {
    safeDisconnect(sourceRef.current)
    safeDisconnect(workletNodeRef.current)
    safeDisconnect(sinkRef.current)
    safeDisconnect(analyserNodeRef.current)
    sourceRef.current = null
    workletNodeRef.current = null
    sinkRef.current = null
    analyserNodeRef.current = null
    setAnalyser(null)

    if (kickstartElRef.current) {
      kickstartElRef.current.pause()
      kickstartElRef.current.srcObject = null
      kickstartElRef.current = null
    }

    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) t.stop()
      streamRef.current = null
    }
  }, [])

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    teardownGraph()
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => undefined)
      audioCtxRef.current = null
      workletReadyRef.current = false
    }
    if (livenessTimerRef.current) {
      clearTimeout(livenessTimerRef.current)
      livenessTimerRef.current = null
    }
    chunksRef.current = []
    metaRef.current = null
  }, [teardownGraph])

  const abortNoSignal = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (livenessTimerRef.current) {
      clearTimeout(livenessTimerRef.current)
      livenessTimerRef.current = null
    }
    teardownGraph()
    chunksRef.current = []
    metaRef.current = null
    setQuality(null)
    setState('idle')
    toast.error(
      t`No sound is coming from your microphone. If you opened this page inside another app, open it in Chrome or Safari.`
    )
  }, [teardownGraph])

  useEffect(() => () => cleanup(), [cleanup])

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl)
    }
  }, [audioUrl])

  const ensureContext = useCallback(async (desiredSampleRate?: number): Promise<AudioContext> => {
    if (!audioCtxRef.current) {
      const Ctx =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      try {
        audioCtxRef.current = desiredSampleRate ? new Ctx({ sampleRate: desiredSampleRate }) : new Ctx()
      } catch {
        audioCtxRef.current = new Ctx()
      }
    }
    const ctx = audioCtxRef.current
    if (ctx.state === 'suspended') await ctx.resume()
    if (ctx.state !== 'running') throw new Error('audio-context-not-running')
    if (!workletReadyRef.current) {
      await ctx.audioWorklet.addModule(WORKLET_URL)
      workletReadyRef.current = true
    }
    return ctx
  }, [])

  const startRecording = useCallback(async () => {
    if ((state !== 'idle' && state !== 'recorded') || startingRef.current) return
    startingRef.current = true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1
        }
      })
      streamRef.current = stream
      const micTrack = stream.getAudioTracks()[0]
      if (micTrack) micTrack.onended = () => stopRef.current()

      const kickstart = new Audio()
      kickstart.srcObject = stream
      kickstart.muted = true
      kickstart.play().catch(() => undefined)
      kickstartElRef.current = kickstart

      const ctx = await ensureContext(micTrack?.getSettings().sampleRate)
      sampleRateRef.current = ctx.sampleRate

      const source = ctx.createMediaStreamSource(stream)
      const node = new AudioWorkletNode(ctx, WORKLET_NAME)
      const sink = ctx.createGain()
      sink.gain.value = 0

      // Waveform taps this same capture; a 2nd getUserMedia would mute the recording on Android.
      const analyserNode = ctx.createAnalyser()
      analyserNode.fftSize = 2048
      source.connect(analyserNode)
      analyserNodeRef.current = analyserNode
      setAnalyser(analyserNode)

      chunksRef.current = []
      node.port.onmessage = (e: MessageEvent<Float32Array | 'done'>) => {
        if (e.data === 'done') return
        chunksRef.current.push(e.data)
      }

      source.connect(node)
      node.connect(sink)
      sink.connect(ctx.destination)

      sourceRef.current = source
      workletNodeRef.current = node
      sinkRef.current = sink

      if (captureMetadata) {
        const meta = readAudioClientMetadata(stream)
        meta.encodedFormat = 'wav'
        meta.bitsPerSample = 16
        meta.encodedSampleRate = ctx.sampleRate
        meta.kickstart = true
        meta.ctxState = ctx.state
        if (micTrack) meta.trackMutedAtStart = micTrack.muted
        metaRef.current = meta
      }

      setQuality(null)
      setSeconds(0)
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000)
      setState('recording')
      livenessTimerRef.current = setTimeout(() => {
        livenessTimerRef.current = null
        if (stoppingRef.current) return
        let maxAbs = 0
        for (const c of chunksRef.current) {
          const m = peak(c)
          if (m > maxAbs) maxAbs = m
        }
        if (chunksRef.current.length === 0 || maxAbs < SILENCE_MAX_ABS) abortNoSignal()
      }, LIVENESS_CHECK_MS)
    } catch (err) {
      teardownGraph()
      metaRef.current = null
      const name = (err as DOMException | undefined)?.name
      const message = (err as Error | undefined)?.message
      if (name === 'NotAllowedError' || name === 'NotFoundError') {
        toast.error(t`Unable to access microphone`)
      } else if (message === 'audio-context-not-running') {
        toast.error(
          t`No sound is coming from your microphone. If you opened this page inside another app, open it in Chrome or Safari.`
        )
      } else {
        toast.error(t`Failed to start recording`)
      }
    } finally {
      startingRef.current = false
    }
  }, [state, captureMetadata, ensureContext, teardownGraph, abortNoSignal])

  const finalize = useCallback((mutedAtStop?: boolean) => {
    const sampleRate = sampleRateRef.current
    const chunks = chunksRef.current
    const samples = concatFloat32(chunks)
    const wavBlob = encodeWav(chunks, sampleRate)
    chunksRef.current = []
    const result = analyzeBuffer(samples)
    if (metaRef.current) {
      metaRef.current.clientQuality = {
        issue: result.issue,
        voiceRatio: sanitizeMetric(result.voiceRatio),
        peakDb: sanitizeMetric(result.peakDb),
        voiceDb: sanitizeMetric(result.voiceDb),
        snr: sanitizeMetric(result.snr)
      }
      if (mutedAtStop !== undefined) metaRef.current.trackMutedAtStop = mutedAtStop
    }
    setQuality({ result, blocked: result.issue === 'no-signal' || result.issue === 'no-voice' })
    setBlob(wavBlob)
    setAudioUrl(URL.createObjectURL(wavBlob))
    setState('recorded')
  }, [])

  const flushWorklet = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const node = workletNodeRef.current
      if (!node) {
        resolve()
        return
      }
      let resolved = false
      const settle = () => {
        if (resolved) return
        resolved = true
        clearTimeout(timeoutId)
        resolve()
      }
      const timeoutId = setTimeout(settle, 250)
      const previous = node.port.onmessage
      node.port.onmessage = (e: MessageEvent<Float32Array | 'done'>) => {
        if (e.data === 'done') {
          node.port.onmessage = previous
          settle()
          return
        }
        if (e.data) chunksRef.current.push(e.data)
      }
      try {
        node.port.postMessage('flush')
      } catch {
        settle()
      }
    })
  }, [])

  const stopRecording = useCallback(async () => {
    if (stoppingRef.current || state !== 'recording') return
    stoppingRef.current = true
    if (livenessTimerRef.current) {
      clearTimeout(livenessTimerRef.current)
      livenessTimerRef.current = null
    }
    try {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      safeDisconnect(sourceRef.current)
      await flushWorklet()
      const mutedAtStop = streamRef.current?.getAudioTracks()[0]?.muted
      teardownGraph()
      finalize(mutedAtStop)
    } finally {
      stoppingRef.current = false
    }
  }, [state, teardownGraph, finalize, flushWorklet])

  useEffect(() => {
    stopRef.current = () => {
      void stopRecording()
    }
  }, [stopRecording])

  const reset = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioUrl(null)
    setBlob(null)
    setSeconds(0)
    setQuality(null)
    metaRef.current = null
  }, [audioUrl])

  const resetToIdle = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioUrl(null)
    setBlob(null)
    setSeconds(0)
    setQuality(null)
    setState('idle')
    metaRef.current = null
  }, [audioUrl])

  const markSubmitting = useCallback(() => setState('submitting'), [])
  const markSubmitted = useCallback(() => setState('submitted'), [])
  const markRecorded = useCallback(() => setState('recorded'), [])

  const getClientMetadata = useCallback(() => metaRef.current, [])

  return {
    state,
    seconds,
    audioUrl,
    blob,
    quality,
    analyser,
    startRecording,
    stopRecording,
    reset,
    resetToIdle,
    markSubmitting,
    markSubmitted,
    markRecorded,
    getClientMetadata
  }
}
